import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchOperationsData } from '../../store/slices/operationsSlice';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Modal } from '../../components/ui/modal';
import TransactionReceipt from '../../components/transactions/TransactionReceipt';
import { PREDEFINED_AMOUNTS, PROVIDERS } from '../../lib/constants';
import { Method, operationsService } from '../../services/api/operationsService';
import { addTransaction } from '../../store/slices/transactionsSlice';
import { printService } from '../../services/printService';
import { Transaction } from '../../types';

const STORAGE_BASE = 'https://kaoxet.net/storage/';
const fmt = (n: number) =>
  new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP', minimumFractionDigits: 2 }).format(n);

const RecargasPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { wallet }         = useAppSelector((state) => state.wallet as any);
  const { rechargeMethods } = useAppSelector((state) => state.operations as any);
  const fetchedRef = useRef(false);

  const [phoneNumber,       setPhoneNumber]      = useState('');
  const [amount,            setAmount]           = useState<number | ''>('');
  const [selectedProvider,  setSelectedProvider] = useState('');
  const [isProcessing,      setIsProcessing]     = useState(false);
  const [error,             setError]            = useState<string | null>(null);
  const [success,           setSuccess]          = useState<string | null>(null);
  const [showReceiptModal,  setShowReceiptModal]  = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState<Transaction | null>(null);

  useEffect(() => {
    if (!fetchedRef.current && (!rechargeMethods || rechargeMethods.length === 0)) {
      fetchedRef.current = true;
      dispatch(fetchOperationsData());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const providerList: Method[] =
    rechargeMethods && rechargeMethods.length > 0 ? rechargeMethods : PROVIDERS.RECARGAS;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber || phoneNumber.length < 10) { setError('Ingrese un número de teléfono válido de 10 dígitos'); return; }
    if (!selectedProvider)  { setError('Seleccione una compañía'); return; }
    if (!amount)            { setError('Seleccione o ingrese un monto'); return; }
    if (wallet && wallet.balance < Number(amount)) { setError('Saldo insuficiente para realizar esta recarga'); return; }

    setIsProcessing(true); setError(null);
    try {
      const res = await operationsService.recharge(phoneNumber, Number(amount), selectedProvider);
      // LOG para ver los campos reales que devuelve el API
      console.log('[Recargas] API response data:', JSON.stringify(res.data));
      if (!res.success) throw new Error(res.message || 'Error al procesar la recarga');

      const providerName = providerList.find(p => p.id === selectedProvider)?.name || selectedProvider;
      // Intentar varios nombres de campo posibles para el ID de transacción
      const serverTxId = res.data?.xid ?? res.data?.transactionId ?? res.data?.id ??
                         res.data?.transaction_id ?? res.data?.reference ?? null;
      const transaction: any = {
        id:        serverTxId || `rec-${Date.now()}`,
        xid:       serverTxId || undefined,
        type:      'RECARGA' as const,
        amount:    Number(amount),
        provider:  providerName,
        date:      new Date().toISOString(),
        status:    'COMPLETED' as const,
        reference: Math.random().toString(36).substring(2, 10).toUpperCase(),
        url:       res.data?.url || null,
        details:   { phoneNumber, amount: Number(amount), provider: providerName },
      };
      dispatch(addTransaction(transaction));
      setSuccess(`Recarga de ${fmt(Number(amount))} completada al número ${phoneNumber}`);
      setCurrentTransaction(transaction);
      setShowReceiptModal(true);
      setPhoneNumber(''); setAmount(''); setSelectedProvider('');
    } catch (err: any) {
      setError(err.message || 'Error al procesar la recarga. Intente nuevamente.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <svg className="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/>
          </svg>
          Recargas
        </h1>
        <p className="text-slate-500 text-sm mt-1">Realice recargas a cualquier operador.</p>
      </div>

      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
          </svg>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-4 mb-5">
          <svg className="w-5 h-5 text-green-500 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
          </svg>
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Compañía */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Compañía</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {providerList.map((p: Method) => (
              <button key={p.id} type="button" onClick={() => { setSelectedProvider(p.id); setError(null); }}
                className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all duration-150 hover:border-blue-300"
                style={{
                  borderColor: selectedProvider === p.id ? '#3b82f6' : '#e2e8f0',
                  background:  selectedProvider === p.id ? '#eff6ff' : '#fff',
                }}>
                <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center overflow-hidden">
                  {p.logo
                    ? <img src={`${STORAGE_BASE}${p.logo}`} alt={p.name} className="w-10 h-10 object-contain"/>
                    : <span className="text-lg font-bold text-slate-400">{p.name.charAt(0)}</span>
                  }
                </div>
                <span className="text-xs font-medium text-slate-600 text-center leading-tight">{p.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Teléfono */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Número de teléfono</p>
          <Input
            label="Teléfono (10 dígitos)"
            value={phoneNumber}
            onChange={e => { const v = e.target.value.replace(/\D/g,''); if (v.length <= 10) setPhoneNumber(v); }}
            placeholder="8091234567" type="tel" fullWidth leftAddon="+1"
            useCustomKeyboard={false}
          />
        </div>

        {/* Monto */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Monto a recargar</p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
            {PREDEFINED_AMOUNTS.map(n => (
              <button key={n} type="button" onClick={() => { setAmount(n); setError(null); }}
                className="py-3 rounded-xl border-2 text-sm font-bold transition-all duration-150"
                style={{
                  borderColor: amount === n ? '#3b82f6' : '#e2e8f0',
                  background:  amount === n ? '#eff6ff' : '#fff',
                  color:       amount === n ? '#1d4ed8' : '#475569',
                }}>
                {fmt(n)}
              </button>
            ))}
          </div>
          <Input
            label="Otro monto"
            value={amount === '' ? '' : String(amount)}
            onChange={e => { const v = e.target.value.replace(/\D/g,''); setAmount(v === '' ? '' : parseInt(v, 10)); }}
            placeholder="Ingrese un monto" fullWidth leftAddon="RD$"
            useCustomKeyboard={false}
          />
        </div>

        {/* Resumen */}
        {selectedProvider && amount !== '' && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-blue-700 font-medium">
                {providerList.find(p => p.id === selectedProvider)?.name}
              </p>
              {phoneNumber && <p className="text-xs text-blue-500">+1 {phoneNumber}</p>}
            </div>
            <span className="text-2xl font-black text-blue-600">{fmt(Number(amount))}</span>
          </div>
        )}

        <Button type="submit" size="touch" fullWidth isLoading={isProcessing}
          disabled={!phoneNumber || phoneNumber.length < 10 || !selectedProvider || amount === ''}>
          {isProcessing ? 'Procesando...' : '✓ Realizar Recarga'}
        </Button>
      </form>

      <Modal isOpen={showReceiptModal} onClose={() => setShowReceiptModal(false)}
        title="Resumen de Transacción" size="md">
        {currentTransaction && (
          <TransactionReceipt transaction={currentTransaction}
            onClose={() => setShowReceiptModal(false)}
            onPrint={() => { if (currentTransaction) printService.printTicket(currentTransaction); }}
          />
        )}
      </Modal>
    </div>
  );
};

export default RecargasPage;
